import { CatalogAccessType, PlatformCatalogDTO, useGetPlatformCatalogById } from "@bvhi/backend-api/apis";
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from "@bvhi/ui-base";
import { NavigateNextIcon, RuleFolderIcon, RuleIcon } from "@bvhi/ui-icons";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Fragment } from "react/jsx-runtime";
import { useAuthContext } from "../../../../../../context/use-auth-context";
import { useMatchingRules } from "../../../../../../hooks/useMatchingRules";
import { toNarrowedTypeOrNull } from "../../../../../../utils/ts";
import { PlatformCatalogAccessTypeChip } from "../../components/platform-catalog-access-type-chip";

export const CatalogRulesCard = () => {
  const { catalogId } = useParams({ strict: false });
  if (!catalogId) throw new Error("catalogId not found in path");

  const { currentUser } = useAuthContext();
  const { t } = useTranslation(["catalogs", "common", "enums"]);
  const navigate = useNavigate();
  const { getMatchingCriteriaStringRepresentation } = useMatchingRules();

  const { data: getPlatformCatalogByIdResponse, isError } = useGetPlatformCatalogById(currentUser?.organisation?.id ?? "", catalogId);

  const catalog = toNarrowedTypeOrNull<PlatformCatalogDTO>(getPlatformCatalogByIdResponse?.data, "id");

  if (!catalog || isError) return;

  let subHeader = "";
  switch (catalog.platformCatalogAccessType) {
    case CatalogAccessType.PUBLIC:
      subHeader = t("catalogs:catalogs.cards.rules.card.type.public.description");
      break;

    case CatalogAccessType.ALL_CUSTOMERS:
      subHeader = t("catalogs:catalogs.cards.rules.card.type.all.customers.description");
      break;

    case CatalogAccessType.PROTECTED:
      subHeader = t("catalogs:catalogs.cards.rules.card.type.protected.description");
      break;
  }

  return (
    <Card>
      <CardHeader
        subheader={subHeader}
        title={
          <Stack alignItems="center" flexDirection="row" justifyContent="flex-start" marginBottom={1} spacing={1}>
            <Typography variant="h5">{t("catalogs:catalogs.cards.rules.card.title")}</Typography>
            <PlatformCatalogAccessTypeChip size="medium" status={catalog.platformCatalogAccessType} />
          </Stack>
        }
      />
      <CardContent>
        {catalog.platformCatalogAccessType === CatalogAccessType.PROTECTED && (
          <>
            {catalog.platformCatalogAccessMatchingBlocks.length < 1 && (
              <Typography fontWeight="bold">{t("catalogs:catalogs.cards.rules.card.no.ruleset")}</Typography>
            )}

            {catalog.platformCatalogAccessMatchingBlocks.length >= 1 && (
              <List
                dense
                sx={{
                  padding: 0,
                  margin: 0,
                }}
              >
                {catalog.platformCatalogAccessMatchingBlocks.map((matchingBlock, index) => {
                  return (
                    <Fragment key={index}>
                      <ListItem
                        sx={{
                          paddingLeft: 0,
                          paddingTop: 3,
                          "&:first-of-type": {
                            paddingTop: 0,
                          },
                        }}
                      >
                        <ListItemIcon>
                          <RuleFolderIcon />
                        </ListItemIcon>
                        <ListItemText primary={`Regelsatz ${index + 1}`} />
                      </ListItem>
                      <List component="div" dense disablePadding>
                        {matchingBlock.platformCatalogAccessMatchingRules.length < 1 && (
                          <ListItem sx={{ pl: 4 }}>
                            <ListItemIcon>
                              <RuleIcon />
                            </ListItemIcon>
                            <ListItemText primary={t("catalogs:catalogs.cards.rules.card.no.rule")} />
                          </ListItem>
                        )}
                        {matchingBlock.platformCatalogAccessMatchingRules.length >= 1 &&
                          matchingBlock.platformCatalogAccessMatchingRules.map((matchingRule, nestedIndex) => (
                            <ListItem key={`${index}-${nestedIndex}`} sx={{ pl: 4 }}>
                              <ListItemIcon>
                                <RuleIcon />
                              </ListItemIcon>
                              <ListItemText
                                primary={`${matchingRule.column} ${getMatchingCriteriaStringRepresentation(matchingRule.criteria)} ${matchingRule.value}`}
                              />
                            </ListItem>
                          ))}
                      </List>
                    </Fragment>
                  );
                })}
              </List>
            )}
          </>
        )}
      </CardContent>
      <CardActions>
        <Button
          key={1}
          onClick={() => void navigate({ to: "/app/catalogs/catalog/$catalogId/rules", params: { catalogId } })}
          startIcon={<NavigateNextIcon />}
          variant="contained"
        >
          {t("catalogs:catalogs.ruleset.button.update")}
        </Button>
      </CardActions>
    </Card>
  );
};
